import CONSTANTS from "./Constants";
import { gsap } from "gsap";
import MotionPathPlugin from 'gsap/MotionPathPlugin'
import ScrollTrigger from 'gsap/ScrollTrigger'
import Flip from 'gsap/Flip'
import { SlowMo } from "gsap/EasePack";
import MouseGyroListener from './MouseGyroListener';
import { getMoveAnimation } from "./animations/move";
import { getKeyframeAnimation } from "./animations/keyframe";
import { getStepAnimation } from "./animations/fadepos";
import { getFadeAnimation } from "./animations/fade";
import { getFlipAnimation } from "./animations/flip";
import { getMaskedMoveAnimation, getMaskedZoomAnimation } from "./animations/masked";
import { getManualAnimation } from "./animations/manual";
import { getRotateAnimation } from "./animations/rotate";
import { getZoomAnimation } from "./animations/zoom";
import {getBlurAnimation} from "./animations/blur";
import { SplitText } from 'gsap/SplitText'
import { addTextAnimation, getTextAnimation, hasTextAnimation, prepareTextAnimations } from './textanimations'
import { getAllTextTargets } from './textanimations/splitText'
import {createShadowElementForTrigger, getOffsetParentsOffset} from "./Utils"
import {eases} from "./animations/eases";
import {
    computeInStartTrigger,
    computeOutStartTrigger,
    computeStickyUntilSceneEnd,
    needsShadowTriggerForOut
} from "./AnimationUtils";

const DebugPromise = () => import(/* webpackChunkName: "debug" */ './plugins/debug.js');

export default class Animations{

    constructor(scene, animations){
        gsap.registerPlugin(ScrollTrigger, MotionPathPlugin, SlowMo, Flip, SplitText)

        if (process.env.NODE_ENV === 'development') {
            DebugPromise().then(({onInitDebug}) => {
                onInitDebug(gsap)
            }).catch((e) =>{
                console.error(e);
            });
        }

        // if (window.matchMedia('(prefers-reduced-motion)').matches){
        //     console.log('prefers-reduced-motion')
        // }
        this.settings = animations;
        this.scene = scene;
        this.closeAnimations = {}
        this.openAnimations = {}
        this.scrollTriggers = []
        this.motionPaths = []
        this.lastViewPortHeight = this.getAnimationViewportHeight()

        this.timer = gsap.delayedCall(0.25, () => this.resizeHandler()).pause()
    }

    onWindowResize () {
        this.timer.restart(true)
        !this.pausedByResize && (this.pausedByResize = !this.mainTL.paused())
        this.mainTL.pause();
    }

    reInitMotionPath(motionPath) {
        // ScrollTrigger.killAll();
        // this.motionPaths[0].trigger.kill()
        // this.motionPaths[0].timeline.invalidate()
        // this.motionPaths[0].trigger = ScrollTrigger.create(this.motionPaths[0].settings)
        // this.motionPaths[0].trigger.refresh()
        // ScrollTrigger.refresh()

        const previousTrigger = motionPath.trigger;
        const wasEnabled = previousTrigger.enabled;
        previousTrigger.kill()
        motionPath.timeline.invalidate()
        motionPath.trigger = this.createScrollTrigger(motionPath.settings)
        const index = this.scrollTriggers.indexOf(previousTrigger);
        if (index !== -1) this.scrollTriggers[index] = motionPath.trigger;
        motionPath.trigger.refresh()
        if (wasEnabled === false) motionPath.trigger.disable(false);
    }

    resizeHandler () {
        this.onWindowResize()
        if (Math.abs(this.lastViewPortHeight - this.getAnimationViewportHeight()) > 0) {
            this.lastViewPortHeight = this.getAnimationViewportHeight()

            this.motionPaths.forEach( motionPath => this.reInitMotionPath(motionPath))

            // this.motionPaths.forEach(({timeline, trigger = undefined, settings = {}}, i) => {
            //     timeline.invalidate()
            //     if (trigger && settings.pin) {
            //         settings.pin.style.marginTop = '' // remove previously added marginTop
            //         trigger.kill()
            //         const newTrigger = ScrollTrigger.create(settings)
            //         this.motionPaths[i].trigger = newTrigger
            //         const index = this.scrollTriggers.findIndex(scrollTrigger => trigger === scrollTrigger )
            //         this.scrollTriggers[index] = newTrigger
            //     }
            // })
            //
            // this.scrollTriggers.forEach(scrollTrigger => scrollTrigger.refresh()) // refresh all scrollTriggers for this scene
        }
        this.pausedByResize && this.mainTL.play()
        this.pausedByResize = false
    };

    createScrollTrigger(settings) {
        return ScrollTrigger.create({ ...settings, scroller: this.scene.spread.getAnimationScroller() });
    }

    getAnimationViewportHeight() {
        return this.scene.spread.scrollContext.element ? this.scene.spread.getWindowHeight() : window.visualViewport.height;
    }

    refreshScrollContainer() {
        if (!this.mainTL) return; // Scenes outside the viewport may not have initialized yet.
        this.sceneScale = this.getSceneScale(this.scene.getSceneElement(false));
        this._animationcalculation.scale = this.sceneScale;
        this._animationcalculation.viewport = {
            width: this.scene.spread.getWindowWidth(),
            height: this.scene.spread.getWindowHeight(),
        };
        this.motionPaths.forEach(path => this.reInitMotionPath(path));
        this.scrollTriggers.forEach(trigger => trigger.refresh());
    }

    disableScrollTriggers () {
        this.scrollTriggers.forEach(trigger => trigger.disable(false))
    }

    // killScrollTriggers () {
    //     if (this.mainTL) {
    //         this.mainTL.progress(1)
    //     }
    //     this.scrollTriggers.forEach(trigger => {
    //         trigger.animation && trigger.animation.progress(1) // only set progress for animations, not reset triggers
    //         trigger.kill()
    //     })
    // }

    enableScrollTriggers () {
        this.scrollTriggers.forEach(trigger => trigger.enable())
    }

    getSceneScale (scene) {
        return parseFloat(scene.style.getPropertyValue('--sceneScale')) || 1
    }

    init(){
        this.closeAnimations = {};
        this.openAnimations = {};
        this.scrollTriggers = [];
        this.motionPaths = [];

        const scene = this.scene.getSceneElement();
        this.sceneScale = this.getSceneScale(scene[0])

        if (this.refreshInitHandler) ScrollTrigger.removeEventListener("refreshInit", this.refreshInitHandler);
        this.refreshInitHandler = () => {
            this.sceneScale = this.getSceneScale(scene[0])
        };
        ScrollTrigger.addEventListener("refreshInit", this.refreshInitHandler);

        if(scene.length !== 1){
            console.error("Scene not found: ", scene);
        }
        this._animationcalculation = {
            scene_uuid: this.scene.uuid,
            scene: scene[0],
            pagetype: this.scene.pagetype ,
            maglrspread: scene.find(".maglrspread") ,
            scene_offset: 0,
            spreadOffset: 0,
            pxemratio: scene.data('pxemratio'),
            viewport: {
                width: this.scene.spread.getWindowWidth(),
                height: this.scene.spread.getWindowHeight(),
            }
        };
        if (this.scene.spread.injected) {
            this._animationcalculation.spreadOffset = getOffsetParentsOffset(scene[0])
        }
        this._animationcalculation.scale = this.scene.getSizeData().scale ?? 1;

        let scene_offset = this.scene.getSceneElement(false).offsetTop || 0,
            current_scroll = this.scene.spread.getScrollTop()
        ;

        if(scene_offset !== current_scroll ){
            if(this.scene.spread.active_layout === 2){
                this._animationcalculation.scene_offset = (current_scroll + this.scene.spread.getWindowHeight()) - ((scene_offset * this._animationcalculation.scale) + (this.scene.spread.getWindowHeight() /this._animationcalculation.scale) )
            }else{
                this._animationcalculation.scene_offset = current_scroll - scene_offset // subtract the sceneOffset like the scroll does.
            }
        }

        this.setAnimationCalculationFunctions();

        this.mainTL = gsap.timeline({ id: this.scene.uuid, paused: true, uuid: this.scene.uuid, removed : [], data: {} });
        this.createTimeline(this.settings.children, this.mainTL);

        scene.find(".not_initialized").removeClass("not_initialized");

        this.scene.spread.getSpreadElement(true).trigger(CONSTANTS.EVENT_AFTER_ANIMATION_INIT, [this.scene.uuid]);

        this.scene.getSceneElement()
            .on("mouseover", ".hover-animation", (event) => {
                this.toggleLoopAnimationDone(event.currentTarget, true);
            })
            .on("mouseout", ".hover-animation:not([data-loop-count])", (event) => {
                this.toggleLoopAnimationDone(event.currentTarget, false);
            })
        ;

        this.bindParallaxEffects();

        return true;
    }

    createTimeline(animations, parent_timeline, parentCloseline) {
        if (animations) {
            for (let uuid in animations) {
                let alternative_timeline, element, types, timeline ;
                alternative_timeline = false;
                element = this.scene.runSelector("#" + uuid);

                if(element.length === 0){
                    console.error(`Animation error, element [${uuid}] not found`);
                    continue;
                }
                types = animations[uuid];
                if (hasTextAnimation(types)) {
                    prepareTextAnimations(element[0], types);
                }
                timeline = gsap.timeline( {id: uuid, uuid, removed : [], data: {}  } );
                let closeline = null
                let skipLoopAnimation = false;

                if (parent_timeline.data.isTriggerControlled) {
                    timeline.data.isTriggerControlled = true
                }

                //Initialize the in, loop and outgoing animations. The order in which this is done is important because of the following:
                //first init the out-animation because it doesn't effect the elements current position.
                //second init the in-animation because it will put the element on its start position and it will effect other calculations

                const resetTriggers = {out: false, in: false, trigger: null}

                if ( types.out ) {
                    let animation_out = this.getAnimation(element, types.out, 'out');
                    if(types.out.duration > 0 || types.out.trigger?.scrubDuration > 0 || types.out.type === 'children'){
                        if( types.out.close){ // close animation so add to close_animations array

                            closeline = gsap.timeline({id: uuid, paused: true, uuid: uuid, data: {uuid, removed:[]} });

                            if( animation_out.frames ){
                                let i, length = animation_out.frames.length, tl_position = types.out.start;
                                for(i = 0; i < length; i++){
                                    closeline.fromTo( element, animation_out.frames[i].from, animation_out.frames[i].to, tl_position);
                                    //add duration so the next keyframe can be added on that spot
                                    tl_position += animation_out.frames[i].to.duration;
                                }
                                this.motionPaths.push({timeline:closeline})
                            }else if (animation_out.text) {
                                addTextAnimation(closeline, element, animation_out, types.out.duration, types.out.start, 'out');
                            }else{
                                animation_out.duration = types.out.duration
                                animation_out.immediateRender = false
                                closeline.to(element, animation_out, types.out.start );
                            }

                            closeline.data.isTriggerControlled = types.out.trigger?.scrubDuration > 0 || parentCloseline?.data.isTriggerControlled

                            closeline.addLabel("start-close", types.out.start);
                            closeline.addLabel("end-close", types.out.start+types.out.duration);
                            if (!closeline.data.isTriggerControlled) {
                                closeline.call( () => this.onAnimationStart(element, 'out'), null, "start-close");
                                closeline.call( () => this.closeAnimationDone(element), null, "end-close");
                            }

                            this.closeAnimations[uuid] = closeline;
                            if (parentCloseline) {
                                parentCloseline.add(closeline.paused(false), 0)
                                closeline.data.parent = parentCloseline.vars.uuid

                                if (!closeline.data.isTriggerControlled) {
                                    parentCloseline.eventCallback('onComplete', () => {
                                        this.closeAnimationDone(this.scene.runSelector(`#${parentCloseline.data.uuid}`))
                                    })
                                }
                            }

                            //Any scroll triggers?
                            if( types.out.trigger && types.out.trigger.hasOwnProperty("point") && !parentCloseline?.data?.isTriggerControlled  ){
                                closeline.data.isTriggerControlled = true

                                const scrollTriggerSettings = {
                                    animation: closeline,
                                    trigger: needsShadowTriggerForOut(types.out.trigger) ? createShadowElementForTrigger(element[0], this.scene.getSceneElement(false), {direction: 'out', maskedMove: types.out.type.startsWith('masked'), stickyFor: types.out.trigger.stickyFor}) : element[0],
                                    start: `${types.out.triggerPosition ?? 'top'} ${types.out.trigger.point}%`,
                                    // markers: {startColor: "purple", endColor: "orange"},
                                    toggleActions: ['play', 'none', 'none', 'none'].join(' '),
                                    refreshPriority: 1,
                                }

                                if (types.out.trigger.onSticky) {
                                    // scrollTriggerSettings.markers = {startColor: "pink", endColor: "orange"}
                                    scrollTriggerSettings.start = () => {
                                        return computeOutStartTrigger(types.out.trigger, this.sceneScale, this._animationcalculation.containerOffset)
                                    }
                                }

                                if (types.out.trigger.scrubDuration > 0 || types.out.trigger.durationUnit === 'sticky') {
                                    scrollTriggerSettings.scrub = true
                                    scrollTriggerSettings.toggleClass = {targets: element[0], className: 'scrubbing-out'}

                                    if (types.out.trigger.durationUnit === 'sticky') {
                                        if (types.out.trigger.stickyForPx > 0) {
                                            scrollTriggerSettings.end = () => {
                                                return `+=${(types.out.trigger.stickyForPx * this.sceneScale)}` // checked
                                            }
                                        } else {
                                            scrollTriggerSettings.endTrigger = this._animationcalculation.scene
                                            scrollTriggerSettings.end = () => {
                                                return computeStickyUntilSceneEnd(types.out.trigger, element[0].offsetHeight, this.sceneScale, this._animationcalculation.containerOffset)
                                            }
                                        }
                                    }else if (types.out.trigger.durationUnit === 'percentage') {
                                        scrollTriggerSettings.end = `+=${types.out.trigger.scrubDuration}%`
                                    } else {
                                        scrollTriggerSettings.end = () => {
                                            return `+=${(types.out.trigger.scrubDuration * this.sceneScale)}`
                                        }
                                    }
                                    if (types.loop) {
                                        scrollTriggerSettings.onEnter = () => {
                                            element[0].classList.remove('loop-animation')
                                            element[0].classList.add('animating')
                                        }
                                        scrollTriggerSettings.onLeaveBack = () => {
                                            element[0].classList.add('loop-animation')
                                            element[0].classList.remove('animating')
                                        }
                                    }
                                }
                                const scrollTrigger = this.createScrollTrigger(scrollTriggerSettings)
                                scrollTrigger.disable()
                                this.scrollTriggers.push(scrollTrigger)

                                if (types.out.trigger.reset) {
                                    resetTriggers.out = closeline
                                }
                            }

                        }else{

                            if( animation_out.frames ){
                                let i, length = animation_out.frames.length, tl_position = types.out.start;
                                for(i = 0; i < length; i++){
                                    timeline.fromTo( element, animation_out.frames[i].from, animation_out.frames[i].to, tl_position);
                                    //add duration so the next keyframe can be added on that spot
                                    tl_position += animation_out.frames[i].to.duration;
                                }
                                this.motionPaths.push({timeline})
                            }else if (animation_out.text) {
                                addTextAnimation(timeline, element, animation_out, types.out.duration, types.out.start, 'out');
                            }else{
                                timeline.to(element, types.out.duration, animation_out, types.out.start );
                            }

                            timeline.addLabel("start-out", types.out.start);
                            timeline.addLabel("end-out", types.out.start+types.out.duration);
                            if (!timeline.data.isTriggerControlled) {
                                timeline.call( () => this.onAnimationStart(element, 'out'), null, "start-out");
                                timeline.call( () => this.onAnimationDone(element, 'out'), null, "end-out");
                            }
                        }
                    } else {
                        if (parent_timeline.data.isTriggerControlled) {
                            timeline.to(element, animation_out.text ? animation_out.fallback : animation_out, types.out.start );
                        } else {
                            timeline.call(() => this.scene.toggleAndAction(element), null, types.out.start);
                        }
                    }
                } else {
                    if (types.children && Object.values(types.children).some(child => child.out?.close)) {
                        closeline = gsap.timeline({id: uuid, paused: true, uuid: uuid, data: {uuid, removed:[]}})

                        this.closeAnimations[uuid] = closeline
                        element.addClass("has_animation_close")

                        if (parentCloseline) {
                            closeline.data.isTriggerControlled = types.out?.trigger?.scrubDuration || parentCloseline?.data.isTriggerControlled
                            closeline.data.parent = parentCloseline.vars.uuid
                            parentCloseline.add(closeline.paused(false), 0)
                        }
                    }
                }

                if (types.in) {
                    if(types.in.start > 0){
                        element.addClass("delayed_animation");
                    }
                    if (element.hasClass('transition-effect')) {
                        element.addClass("overrule-transition");
                    }

                    let animation_in = this.getAnimation(element, types.in, 'in');

                    if( types.in.open && (!parent_timeline.data.isTriggerControlled || element.hasClass('hidden'))) { // open animation so add to open_animations array
                        alternative_timeline = true;
                        timeline.paused( true );
                        if (types.in.duration > 0 || types.in.trigger?.scrubDuration > 0 || types.in.type === 'children' ) {
                            timeline.addLabel("end-open", types.in.start+types.in.duration);

                            //Any scroll triggers?
                            if(types.in.trigger && types.in.trigger.hasOwnProperty("point")){
                                timeline.data.isTriggerControlled = true

                                const trigger = createShadowElementForTrigger(element[0], this.scene.getSceneElement(false), {direction: 'in', maskedMove: types.in.type.startsWith('masked')})
                                const scrollTriggerSettings = {
                                    animation: timeline,
                                    trigger,
                                    start: `${types.in.triggerPosition ?? 'top'} ${types.in.trigger.point}%`,
                                    // markers: {startColor: "black", endColor: "yellow"},
                                    toggleActions: ['play', 'none', 'none', 'none'].join(' '),
                                    refreshPriority: 2,
                                }

                                if (types.in.trigger.onSticky) {
                                    // scrollTriggerSettings.markers = {startColor: "purple", endColor: "orange"}
                                    scrollTriggerSettings.start = () => {
                                        return computeInStartTrigger(types.in.trigger, this.sceneScale, this._animationcalculation.containerOffset)
                                    }
                                }

                                if (types.in.trigger.scrubDuration > 0 || types.in.trigger.durationUnit === 'sticky') {
                                    scrollTriggerSettings.scrub = true
                                    scrollTriggerSettings.toggleClass = {targets: element[0], className: 'scrubbing-in'}
                                    // scrollTriggerSettings.markers = {startColor: "red", endColor: "blue"}

                                    if (types.in.trigger.durationUnit === 'sticky') {
                                        if (types.in.trigger.stickyForPx > 0) {
                                            scrollTriggerSettings.end = () => {
                                                return `+=${(types.in.trigger.stickyForPx * this.sceneScale)}` // checked
                                            }
                                        } else if (types.in.trigger.stickyAnchor) {
                                            scrollTriggerSettings.endTrigger = this._animationcalculation.scene
                                            scrollTriggerSettings.end = () => {
                                                return computeStickyUntilSceneEnd(types.in.trigger, element[0].offsetHeight, this.sceneScale, this._animationcalculation.containerOffset)
                                            }
                                        } else {
                                            // Fallback for a non-sticky element that has sticky scrub settings
                                            scrollTriggerSettings.end = `+=100`
                                            // scrollTriggerSettings.markers = {startColor: "green", endColor: "deeppink"}
                                        }
                                    }else if (types.in.trigger.durationUnit === 'percentage') {
                                        scrollTriggerSettings.end = `+=${types.in.trigger.scrubDuration}%`
                                    } else {
                                        scrollTriggerSettings.end = () => {
                                            return `+=${(types.in.trigger.scrubDuration * this.sceneScale)}`
                                        }
                                    }

                                    if (types.loop) {
                                        skipLoopAnimation = true
                                        scrollTriggerSettings.onEnterBack = () => {
                                            element[0].classList.remove('loop-animation')
                                            element[0].classList.add('animating')
                                        }
                                        scrollTriggerSettings.onLeave = () => {
                                            element[0].classList.add('loop-animation')
                                            element[0].classList.remove('animating')
                                        }
                                    }
                                }
                                const scrollTrigger = this.createScrollTrigger(scrollTriggerSettings)
                                scrollTrigger.disable()
                                this.scrollTriggers.push(scrollTrigger)
                                if (types.in.trigger.reset) {
                                    resetTriggers.in = timeline
                                    resetTriggers.trigger = trigger
                                }

                                element.addClass("delayed_by_scroll");
                            }

                            if( animation_in.frames ){
                                let i, length = animation_in.frames.length, tl_position = types.in.start;

                                for(i = 0; i < length; i++){
                                    timeline.fromTo( element, animation_in.frames[i].from, animation_in.frames[i].to, tl_position);
                                    //add duration so the next keyframe can be added on that spot
                                    tl_position += animation_in.frames[i].to.duration;
                                }
                                this.motionPaths.push({timeline})
                                timeline.progress(1, true).progress(0, true);
                            }else if (animation_in.text) {
                                addTextAnimation(timeline, element, animation_in, types.in.duration, types.in.start, 'in');
                            }else{
                                timeline.from(element, types.in.duration, animation_in, types.in.start);
                            }

                        }else if(types.in.hasOwnProperty("start")){ // only add the toggleElement callback when start is set, else the toggle will be called by the click itself
                            if (!parent_timeline.data.isTriggerControlled) {
                                // Element in root with delay and hidden
                                timeline.call(() => this.scene.toggleAndAction(element), [element], types.in.start);
                            }
                        }

                    } else {

                        if(types.in.duration > 0 || types.in.type === 'children'){
                            if( animation_in.frames ){ // keyframe
                                let i, length = animation_in.frames.length, tl_position = types.in.start;

                                for(i = 0; i < length; i++){
                                    timeline.fromTo( element, animation_in.frames[i].from, animation_in.frames[i].to, tl_position);
                                    //add duration so the next keyframe can be added on that spot
                                    tl_position += animation_in.frames[i].to.duration;
                                }
                                timeline.progress(1, true).progress(0, true);
                            }else if (animation_in.text) {
                                addTextAnimation(timeline, element, animation_in, types.in.duration, types.in.start, 'in');
                            }else{ // others
                                timeline.from(element, types.in.duration, animation_in, types.in.start);
                            }

                        }else{
                            if (parent_timeline.data.isTriggerControlled) {
                                // mutate element so it resets correctly
                                element.removeClass('hidden').removeAttr('data-hidden')
                                timeline.from(element, animation_in.text ? animation_in.fallback : animation_in, types.in.start);
                            } else {
                                timeline.call(() => this.scene.toggleAndAction(element), [element], types.in.start);
                            }
                        }
                    }

                    timeline.addLabel("start-in", types.in.start);
                    timeline.addLabel("end-in", types.in.start+types.in.duration);
                    timeline.call( () => this.onAnimationStart(element, 'in'), null, "start-in");
                    timeline.call( () => this.onAnimationDone(element, 'in'), null, "end-in");

                }else{
                    //add start-in label for children that will be added at this point
                    timeline.addLabel("start-in", 0);
                }

                if (resetTriggers.out || resetTriggers.in) {
                    const scrollTrigger = this.createScrollTrigger({
                        trigger: resetTriggers.trigger ?? element[0],
                        // markers: {startColor: "green", endColor: "pink"},
                        onLeaveBack: () => {
                            this.resetScrollTrigger(element, resetTriggers )
                        }
                    })
                    scrollTrigger.disable()
                    this.scrollTriggers.push(scrollTrigger)
                }


                if ( types.loop && !skipLoopAnimation ) {
                    //gsap loop
                    //var animation_loop = getAnimation(element, types.loop);
                    //animation_loop.repeat = types.loop.repeat; // 1e keer geld niet als repeat.
                    //animation_loop.yoyo = true;
                    ////animation_loop.repeatDelay = 0.1;
                    //timeline.to(element, 0.5, animation_loop, types.loop.start );

                    //css loop
                    if(types.loop.start === 0){
                        types.loop.start = 0.01;
                    }
                    timeline.call(function(element){
                        element.classList.add("loop-animation");
                    }, [element[0]], types.loop.start);
                }

                if(types.scroll){
                    const scrollLine = timeline
                    const animationScroll = this.getAnimation(element, types.scroll, 'scroll');

                    const animation = {
                        animation: scrollLine,
                        // markers: {startColor: "blue", endColor: "red", fontSize: '8px'},
                    }

                    let trigger
                    if (animationScroll.frames) {
                        animation.trigger = types.scroll.trigger.stickyFor ? createShadowElementForTrigger(element[0], this.scene.getSceneElement(false), {direction: 'scroll'}) : element[0];
                        animation.scrub = true
                        animationScroll.ease = "none"
                        animation.ease = "none"

                        let movement = 0
                        if (types.scroll.direction === 'in') {
                            gsap.set(element[0], animationScroll.frames[0].from)
                        }
                        animationScroll.frames.forEach((frame) => {
                            scrollLine.to(element[0], frame.to);
                            movement += frame.to.duration;
                        })

                        if (types.scroll.scrollfixed) {
                            animation.pin = element[0]
                            animation.pinSpacing = false

                            // animation.onRefresh = (self) => {
                            //     if (self.progress > 0) {
                            //         element[0].style.marginTop = `-${Math.abs(window.scrollY - Math.max(self.start, self._startClamp || 0, 0)) }px`;
                            //     }
                            //     console.log('on Refresh', element[0].style.marginTop, self.isActive)
                            // }

                            if (types.children) {
                                // group with scrollFixed animation, force all scrollFixed children to be absolute
                                forceChildrenToAbsolute(types.children)
                            }

                            animation.onUpdate = (self) => {
                                element[0].style.marginTop = `-${Math.abs(self.scroll() - Math.max(self.start, self._startClamp || 0, 0)) }px`;
                            }
                            animation.onLeave = () => {
                                const { x, y } = types.scroll.destination
                                element[0].style.marginTop = `0px`;
                                element[0].style.transform = element[0].style.transform.replace(/translate\([^)]+\)/, `translate(${x}, ${y})`)
                            }



                            animation.onLeaveBack = () => {
                                element[0].style.marginTop = `0px`;
                                if (types.scroll.direction === 'out') {
                                    element[0].style.transform = element[0].style.transform.replace(/translate\([^)]+\)/, `translate(${0}, ${0})`)
                                }
                            }
                        }

                        animation.start = () => {
                            const viewHeight = this.scene.spread.getWindowHeight()
                            const {offsetSpread: offsetToSpread, offsetScene: offsetToScene} = this.recursiveOffsetTop(element[0])

                            if (offsetToScene === offsetToSpread) { // first scene so always clamp
                                return `clamp(${types.scroll.triggerPosition ?? 'top'} ${types.scroll.trigger.point}%)`
                            }

                            if (offsetToScene < viewHeight * (types.scroll.trigger.point / 100)) {
                                return `${types.scroll.triggerPosition ?? 'top'} ${offsetToScene}px`
                            }
                            return `clamp(${types.scroll.triggerPosition ?? 'top'} ${types.scroll.trigger.point}%)`
                        }
                        animation.end = (self) => {
                            return `+=${(movement * this.sceneScale) - Math.min(self._startClamp || 0, 0)}px`
                        }
                        trigger = this.createScrollTrigger(animation)
                        this.motionPaths.push({trigger, settings: animation, timeline: scrollLine})
                    } else {
                        animation.trigger = element[0]
                        animationScroll.ease = "none"
                        animation.ease = "none"
                        scrollLine.to(element[0], animationScroll)
                        animation.scrub = types.scroll.damping || true
                        // animation.markers = {startColor: "yellow", endColor: "black"}
                        animation.start = () => {
                            let viewPortTrigger = 'bottom'
                            const {offsetSpread} = this.recursiveOffsetTop(element[0])
                            if (this.scene.spread.getWindowHeight() > offsetSpread) {
                                viewPortTrigger = `${offsetSpread}px`
                            }
                            return `top ${viewPortTrigger}`
                        }
                        animation.end = () => {
                            // spreadOffset to document + movement + own height + recursive offset from the top
                            if (this.scene.spread.scrollContext.element) {
                                return this.scene.spread.scrollContext.offsetOf(this.scene.spread.getSpreadElement(false)) + element[0].offsetHeight + animationScroll.y + this.recursiveOffsetTop(element[0]).offsetSpread;
                            }
                            return this.scene.spread.getSpreadElement(false).offsetTop + element[0].offsetHeight + animationScroll.y + this.recursiveOffsetTop(element[0]).offsetSpread
                        }
                        trigger = this.createScrollTrigger(animation)
                    }
                    trigger.disable()
                    this.scrollTriggers.push(trigger)
                }

                if ( types.children ) {
                    if (element[0].classList.contains('canvas-sticky')) {
                        // canvas-sticky group, force all scrollFixed children to be absolute
                        forceChildrenToAbsolute(types.children)
                    }
                    timeline.addLabel("start-children", "start-in");
                    timeline.call( () => {this.onAnimationStart(element, 'in-children')}, null, "start-children");
                    this.createTimeline(types.children, types.scroll ? parent_timeline : timeline, closeline);
                    timeline.addLabel("end-children");
                    timeline.call( () => {this.onAnimationDone(element, 'out-children')}, null, "end-children");
                }

                element.removeClass("not_initialized");

                if (alternative_timeline) {
                    this.openAnimations[uuid] = timeline;
                } else if (!types.scroll) {
                    parent_timeline.add(timeline, 'start-in');
                }
            }
        }
    }

    startAnimations( ){
        //add reset-animation-counter class to reset the internal css counter. (this looks to be the only way it can be done)
        this.scene.runSelector(".reset-animation-counter").removeClass("reset-animation-counter");

        let can_play = false;
        if (this.mainTL) {
            can_play = true;
        } else if(this.init()) {
            can_play = true;
        }

        this.enableScrollTriggers()
        if (!window.noScrollTriggerRefresh) {
            // The refresh of a single trigger measures its element as it stands. The global refresh
            // first reverts the animations of all enabled triggers, so a scrubbed element that stood
            // still while the scene was paused (a popup was open) is measured at its unscrolled size.
            ScrollTrigger.refresh()
        }

        if(can_play){
            this.mainTL.play();
        }

        this.bindMouseGyroEffects();
    }

    startParentCloseAnimation(event){
        let parent = $(event.currentTarget),
            uuid = parent.attr("id"),
            done = parent.data("wait-for-child-done") || 0,
            count = parent.data("wait-for-child-close-count")
        ;
        done++;
        // console.log("wait-for-child-close", count, uuid, done);
        if(done === count){
            parent.removeClass("wait-for-child-close").removeData(['wait-for-child-done', 'wait-for-child-close-count']);

            if( this.closeAnimations.hasOwnProperty(uuid) ){
                this.closeAnimations[uuid].restart();
            }else{
                parent.addClass("toggled").addClass("hidden");
                //TODO: reset children
            }
        }else{
            parent.data("wait-for-child-done", done);
        }
    }

    closeAnimationDone(el){
        el.removeClass("animating")
        el.addClass("toggled").addClass("hidden").removeAttr("style");
        this.clearTextFragments(el);
        el.parent().trigger("child-animation-done");
        this.scene.afterElementToggle(el, false);
    }

    onAnimationStart(el, type){
        let classes = ['delayed_animation','delayed_by_scroll','to_be_animated', 'overrule-transition'] ;
        if( type !== 'in-children'){
            classes.push('loop-animation');
        }
        el.addClass("animating").removeClass(classes.join(' ')).data("animatingType", type);

        if(type === "in" || type === "in-children") {
            if (el.hasClass("has_animation_in") || el.hasClass("has_animation_open")) {
                this.scene.afterElementToggle(el, true, "ANIMATION_START");
            }
        }
    }

    onAnimationDone(el, type){
        el.removeClass("animating");
        if(type === "out"){
            this.scene.toggleElement(el);
        }else{
            if( el.hasClass("has_animation_in") || el.hasClass("has_animation_open") ){
                if( el.hasClass("group") ){ // element is group
                    this.scene.afterElementToggle(el, true, "ANIMATION_END", ":not(.has_animation_in)");
                }else if( el.hasClass("embed") && el.children(".file-embed, .url-embed").length > 0 ){ // single embed element
                    this.scene.afterElementToggle(el, true, "ANIMATION_END");
                }
            }
        }
        gsap.set(el, {clearProps:"all"});
        this.clearTextFragments(el);
    }

    // The fragments of a text animation carry their own inline styles after a tween; the element's
    // clearProps does not reach them.
    clearTextFragments(el){
        const fragments = getAllTextTargets(el[0]);
        if (fragments.length > 0) {
            gsap.set(fragments, {clearProps: "all"});
        }
    }

    getAnimation( element, settings , direction){
        let animation;
        //console.log("element", element);
        //Switch the animation based on it's type. Then get the required animation.
        switch(settings.type){
            case "move":
                animation = getMoveAnimation(element, settings, this._animationcalculation);
                break;
            case "fade":
                animation = getFadeAnimation(element, settings, this._animationcalculation);
                break;
            case "rotate":
                animation = getRotateAnimation(element, settings, this._animationcalculation);
                break;
            //case "pulse":
                //animation = Pulse.getPulseAnimation(element, settings);
                //break;
            case "flip":
                animation = getFlipAnimation(element, settings, this._animationcalculation);
                break;
            case "zoom":
                animation = getZoomAnimation(element, settings, this._animationcalculation);
                break;
            case 'fadepos':
                animation = getStepAnimation(element, settings, true);
                break;
            case 'movepos':
                animation = getStepAnimation(element, settings, false);
                break;
            case 'blur':
                animation = Object.assign({}, getStepAnimation(element, settings, false), getBlurAnimation(element, settings));
                break;
            case 'manual':
                animation = getManualAnimation(element, settings);
                break;
            case 'keyframe':
                animation = getKeyframeAnimation(element, settings, this._animationcalculation, direction);
                break;
            case 'maskedMove':
                animation = getMaskedMoveAnimation(element, settings, this._animationcalculation);
                break;
            case 'maskedZoom':
                animation = getMaskedZoomAnimation(element, settings);
                break;
            case 'parallax':
                animation = {
                    y: this.scene.spread.getWindowHeight() * settings.acceleration
                    // y: this.scene.spread.getWindowHeight() * -0.1
                }
                break;
            case 'children':
                animation = {}
                if (settings.start > 0) {
                    animation.duration = 0
                    animation.visibility = 'hidden'
                }
                break;
            case 'timeline_delay':
                animation = {
                    duration: 0,
                    visibility: 'hidden',
                }
                break;
            case 'timeline_toggle':
                animation = {
                    duration: 0,
                    visibility: 'hidden',
                }
                break;
            case 'textFade':
            case 'textScale':
            case 'textRotate':
            case 'textBlur':
            case 'textHighlight':
            case 'textColorSwitch':
            case 'textTypewriter':
                // Describes the per-fragment tween; addTextAnimation builds it on the timeline.
                animation = getTextAnimation(element, settings, direction);
                break;
            default :
                console.warn("animation not yet configured: ", settings.type, settings, element);
                animation = { opacity : 0};
                break;
        }

        if(settings.ease){
            animation.ease = eases[settings.ease]
        }
        animation.force3D = true;

        return animation;
    }

    toggleLoopAnimationDone(element, bool){
        if(bool){
            $(element).addClass("loop-done");
        }else{
            $(element).removeClass("loop-done");
        }
    }

    moveCloseToGlobal (uuid) {
        let closeLine = this.closeAnimations[uuid]

        this.restoreCloseLine(closeLine)

        if (closeLine.data.parent && closeLine.parent) {
            closeLine.parent.data.removed.push(uuid);
            closeLine.parent.remove(closeLine);
            gsap.globalTimeline.add(closeLine);
        }

        return closeLine;
    }

    restoreCloseLine (closeLine) {
        while (closeLine.data.removed.length > 0) {
            const uuid = closeLine.data.removed.shift(); // Remove first item
            const removed = this.getCloseAnimation(uuid);
            removed.progress(0, true);

            gsap.globalTimeline.remove(removed);
            closeLine.add(removed, 0);

            // Recursively restore the whole closeLine
            if (removed.data.removed) {
                this.restoreCloseLine(removed);
            }
        }
    }

    /**
     * Add the in-animation of the given jq_element to the open_animations object so the animation can be replayed as
     * an open-animation.
     *
     * @param {Object} jq_element
     * @param {uuid} uuid
     */
    addInAnimationAsOpen(jq_element, uuid) {
        let removed = this._searchAndRemoveTimelineRecursive(uuid, this.mainTL);

        if(removed){
            this.restoreAnimationsTimelineRecursive(removed);

            removed.pause()
            this.openAnimations[uuid] = removed;
            gsap.globalTimeline.add(removed);
            jq_element.addClass("has_animation_open");
        }
    }

    /**
     * Recursive loop through the given timeline to find the given uuid animation. When found remove the timeline from its
     * parent and return the removed timeline.
     */
    _searchAndRemoveTimelineRecursive(uuid, timeline){
        let remove, i, children = timeline.getChildren(false, false, true);

        for(i = 0; i < children.length; i ++){
            if(children[i].vars.uuid === uuid){
                remove = children[i];
                timeline.remove(remove);
                timeline.vars.removed.push(uuid);
                return remove;
            }else {
                let kids = children[i].getChildren(false, false, true);
                if(kids.length > 0){
                    let result = this._searchAndRemoveTimelineRecursive(uuid, children[i]);
                    if(result){
                        return result;
                    }
                }

            }
        }
        return false;
    }

    /**
     * Recursive loop through the given timeline and call the restore function to restore the removed in-animations
     */
    restoreAnimationsTimelineRecursive(timeline){
        let i, children = timeline.getChildren(false, false, true);

        for(i = 0; i < children.length; i ++){
            this.restoreAnimationsTimelineRecursive(children[i]);
        }

        if( timeline.vars.removed.length > 0 ){
            this._restoreRemovedTimelines(timeline, timeline.vars.removed);
        }
    }

    /**
     * Restore the removed in-animation timelines to the original scene main timeline.
     */
    _restoreRemovedTimelines(timeline, removed){
        for(let i = 0; i < removed.length; i ++){
            this.openAnimations[removed[i]].progress(0, true);

            gsap.globalTimeline.remove(removed[i]);
            timeline.add(this.openAnimations[removed[i]], 'start-in');
            delete (this.openAnimations[removed[i]]);
            this.scene.runSelector("#"+removed[i]).removeClass("has_animation_open");
        }
        timeline.vars.removed = [];
    }


    setAnimationCalculationFunctions(){
        this._animationcalculation.spread_rect = this._animationcalculation.maglrspread[0].getBoundingClientRect();
        this._animationcalculation.container_height = this.scene.getSizeData().height;
        this._animationcalculation.container_width = this.scene.spread.getWindowWidth();
        this._animationcalculation.containerOffset = this.scene.spread.getInterfaceOffset();
    }

    getOpenAnimation(uuid){
        return this.openAnimations[uuid];
    }

    getCloseAnimation(uuid){
        return this.closeAnimations[uuid];
    }

    bindMouseGyroEffects(){
        let scene = this.scene.getSceneElement(),
            elements = this.scene.runSelector(".mousegyro-effect")
        ;


        if(elements.length > 0){
            scene.off("mousegyro.maglr").on("mousegyro.maglr", (event, data) => {
                elements.each((i, el) => {
                    let element = $(el),
                        mousegyro = element.data("mousegyro")
                    ;

                    gsap.to(element, 1, {
                        x: mousegyro.speedX * data.xp,
                        y: mousegyro.speedY * data.yp,
                        ease: eases[mousegyro.ease]
                    })
                });
            });

            this.mouseGyro = new MouseGyroListener(scene);
        }

    }

    updateMouseGyro(scene){
        if(this.mouseGyro){
            this.mouseGyro.updateSceneSize(scene);
        }
    }

    bindParallaxEffects(){
        const windowHeight = this.scene.spread.getWindowHeight()
        this.scene.runSelector(".parallax-effect").each((i, el) => {
            const config = $(el).data("parallax") || {}
            const distance = windowHeight * config.acceleration
            const tween = gsap.to(el, { y: distance, ease: "none" })

            const trigger = this.createScrollTrigger({
                animation: tween,
                trigger: el,
                start: "clamp(top bottom)",
                end: `bottom top-=${Math.round(distance)}`,
                scrub: config.damping ?? true,
            })
            trigger.disable()
            this.scrollTriggers.push(trigger)
        })
    }

    pause(){
        this.disableScrollTriggers()
        if( this.mainTL ){
            this.mainTL.progress(1, false); // deze op false gezet zodat elementen op hidden gaan als ze een uitgaande animatie hebben

            //show all delayed elements
            this.scene.runSelector(".delayed_animation").removeClass("delayed_animation");
        }

        if( this.mouseGyro ){
            this.mouseGyro.destroy();
        }
    }

    destroy(){
        this.timer.kill();
        this.mainTL && this.mainTL.kill();
        this.mouseGyro && this.mouseGyro.destroy();
        this.scrollTriggers.forEach(trigger => trigger.kill());
        this.motionPaths.forEach(({ trigger, timeline }) => {
            if (trigger) trigger.kill();
            timeline.kill();
        });
        Object.values(this.openAnimations).forEach(timeline => timeline.kill());
        Object.values(this.closeAnimations).forEach(timeline => timeline.kill());
        if (this.refreshInitHandler) {
            ScrollTrigger.removeEventListener("refreshInit", this.refreshInitHandler);
            this.refreshInitHandler = null;
        }
        this.scrollTriggers = [];
        this.motionPaths = [];
        this.openAnimations = {};
        this.closeAnimations = {};
    }

    reset(force){
        if( this.mainTL ){
            if( force || this.scene.restart ){
                this.mainTL.pause();
                this.mainTL.progress(0, true);

                this.scrollTriggers.forEach(trigger => {
                    trigger.disable(false)
                    if (trigger.animation) {
                        trigger.animation.progress(0).pause()
                    }
                })
                this.resetDelayedAnimations();
                this.restoreAnimationsTimelineRecursive(this.mainTL);

                //add reset-animation-counter class to reset the internal css counter. (this looks to be the only way it can be done)
                 this.scene.runSelector(".loop-animation").addClass("reset-animation-counter");
            }else{
                this.mainTL.progress(1, false); // deze op false gezet zodat elementen op hidden gaan als ze een uitgaande animatie hebben

                //show all delayed elements
                this.scene.runSelector(".delayed_animation").removeClass("delayed_animation");
            }

            if( this.mouseGyro ){
                this.mouseGyro.destroy();
            }
        }
    }

    resetScrollTrigger(jq_element, timelines) {
        if( parseInt(jq_element.data('hidden')) !== 1){
            jq_element.removeClass("hidden")
        }

        //Reset toggled (grand-)children but only those who aren't hidden in the layerPanel
        jq_element.find(".hidden").not("[data-hidden='1']").removeClass("hidden toggled");
        jq_element.find(".delay_by_timeline[data-hidden='1']").addClass("hidden");

        if (timelines.out) {
            timelines.out.pause(0)
        }

        if (timelines.in) {
            timelines.in.pause(0)
        }
    }

    resetDelayedAnimations(context){
        let selection
        if (context) {
            selection = context.find(".delay_by_timeline:not([data-hidden])")
        } else {
            selection = this.scene.runSelector(".delay_by_timeline:not([data-hidden])")
        }
        selection.length > 0 && selection.addClass("delayed_animation")
    }

    recursiveOffsetTop (element) {
        let offsetSpread = 0
        let last = 0
        while (element && !element.classList.contains('layout')){
            // console.log(element.className, element.offsetTop)
            last = element.offsetTop
            offsetSpread += element.offsetTop
            element = element.parentNode
        }
        return {offsetSpread, offsetScene: offsetSpread - last}
    }
}

function forceChildrenToAbsolute (children) {
    Object.values(children).forEach(({scroll, children}) => {
        if (scroll) {
            scroll.scrollfixed = false
        }
        if (children) {
            forceChildrenToAbsolute(children)
        }
    })
}


const flipTransformProps= [
    'font-size',
    'color',
    'background-color',
    'opacity',
    'background-size',
    'background-position',
    'object-position',
    'object-fit',
    'background-image',
    'fill',
    'fill-opacity',
    '-webkit-text-stroke',
    '-webkit-text-stroke-color',
    'border-top-left-radius',
    'border-top-right-radius',
    'border-bottom-left-radius',
    'border-bottom-right-radius',
    'border-top-width',
    'border-right-width',
    'border-bottom-width',
    'border-left-width',
    'padding-top',
    'padding-right',
    'padding-bottom',
    'padding-left',
].join(',')

export function doFlipAnimation (targets, stateName, animationOptions) {
    targets.forEach(target => target.classList.add('overrule-transition'))
    const state = Flip.getState(targets, { props: flipTransformProps })

    if (stateName === 'default') {
        targets.forEach(target => target.removeAttribute('data-state'))
    } else {
        targets.forEach(target => target.setAttribute('data-state', stateName))
    }
    // if (stateName === 'default') {
    //     // target.classList.remove('data-state')
    // } else {
    //     target.classList.add(`maglrstate-${stateName}`)
    // }

    const {duration = 1, ease = 'none'} = animationOptions

    // animate from the previous state
    Flip.from(state, {
        duration,
        // duration: 5,
        ease: eases[ease],
        toggleClass: 'flipping',
        onComplete: () => {
            targets.forEach(target => target.classList.remove('overrule-transition'))
        },

    })
}
